import { REPEAT_RULE_OPTIONS } from "@/consts";
import type {
  Activity_RepeatingActivity_Fragment,
  Activity_SingleActivity_Fragment,
  MeetingFragment,
  NewsUnion,
  RepeatingMeal,
  RRuleSet,
  RRuleSetInput,
  SingleMeal,
} from "@/graphql/generated-types";
import type { IOption } from "@/interfaces";
import { getEndDate, getStartDate } from "@/stories/utils/dates";
import type {
  EditRepeatTypes,
  RRuleState,
  RRuleSupportedEntityType,
} from "@/types";
import toRRuleInput from "@/helpers/rrules/toRRuleInput";
import toRRuleState from "@/helpers/rrules/toRRuleState";

export type RRuleFeatureState = {
  __typename: RRuleSupportedEntityType;
  id?: number;
  startDate: number;
  endDate: number;
  editRepeat: EditRepeatTypes;
  selectedRruleOption: IOption;
  repeatId?: string;
  rruleState?: RRuleState;
  rset?: RRuleSetInput;
  original?: Partial<RRuleFeatureState>;
};

export type DatabaseModel = {
  __typename?:
    | Activity_RepeatingActivity_Fragment["__typename"]
    | Activity_SingleActivity_Fragment["__typename"]
    | MeetingFragment["__typename"]
    | RepeatingMeal["__typename"]
    | SingleMeal["__typename"]
    | NewsUnion["__typename"];
  repeat?: {
    __typename?: "RepeatRule" | undefined;
    id: string;
    rset: RRuleSet;
  };
};

export type RRuleInput = {
  isRepeat: boolean;
  repeat?: string | null;
  rset: RRuleSetInput;
};

const REVERSE_TYPE_MAP: Record<
  RRuleSupportedEntityType,
  RRuleSupportedEntityType
> = {
  SingleNews: "RepeatingNews",
  RepeatingNews: "SingleNews",
  SingleActivity: "RepeatingActivity",
  RepeatingActivity: "SingleActivity",
  SingleMeeting: "RepeatingMeeting",
  RepeatingMeeting: "SingleMeeting",
  SingleMeal: "RepeatingMeal",
  RepeatingMeal: "SingleMeal",
};

const REPEATING_TYPES: RRuleSupportedEntityType[] = [
  "RepeatingNews",
  "RepeatingActivity",
  "RepeatingMeeting",
  "RepeatingMeal",
];

export const RRULE_FEATURE = {
  getDefault(__typename: RRuleSupportedEntityType): RRuleFeatureState {
    return {
      __typename,
      startDate: getStartDate(),
      endDate: getEndDate(),
      editRepeat: "single",
      selectedRruleOption: REPEAT_RULE_OPTIONS[0]!,
      rruleState: undefined,
      rset: undefined,
    };
  },

  hash(state: RRuleFeatureState) {
    return {
      selectedRruleOption: state.selectedRruleOption,
      rruleState: state.rruleState,
    };
  },

  setState(data: DatabaseModel, state: RRuleFeatureState) {
    state.editRepeat = "single" as EditRepeatTypes;

    if (data.__typename?.toString().startsWith("Repeating") && data.repeat) {
      if (data.repeat.rset) {
        state.rset = data.repeat.rset;
        state.rruleState = toRRuleState(data.repeat.rset);
        state.repeatId = data.repeat.id;
      }
    }
  },

  getItem(state: RRuleFeatureState) {
    const result: Partial<RRuleInput> = {
      isRepeat: false,
      repeat: undefined,
    };

    // Setting the repeat rule for repeating activities
    if (state.rruleState) {
      state.rruleState.startDate = state.startDate;
      result.rset = {
        excludedDates: [],
        rule: toRRuleInput(state.rruleState),
      };
      result.isRepeat = true;
      result.repeat = state.repeatId;
    }

    // We shouldn't marked as repeat on updates if it's not selected a repeat
    if (state.id && state.editRepeat === "single") {
      result.isRepeat = false;
      result.repeat = null;
      result.rset = undefined;
    }

    return result;
  },

  resetTypename(state: RRuleFeatureState) {
    if (
      state.editRepeat === "single" &&
      REPEATING_TYPES.includes(state.__typename)
    ) {
      state.__typename = REVERSE_TYPE_MAP[state.__typename];
    }
  },
};
